import { memo, useEffect, useMemo } from 'react';
import { Clip, LanguageOption } from '@goldcast/player-config/src/types/clip-types';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import SelectDropdown from '@/components/atoms/SelectDropdown';
import Button from '@/components/atoms/Button/Button';
import useTranslationState from '@/hooks/useTranslationState';

interface CaptionLanguageSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (language: LanguageOption) => void;
  clipData: Clip;
}

function CaptionLanguageSelectionDialog({ isOpen, onClose, onConfirm, clipData }: CaptionLanguageSelectionDialogProps) {
  const {
    availableLanguagesForTranslation,
    translatedLanguages,
    originalLanguage,
    selectedLanguage,
    isDisabled,
    handleLanguageChange,
    resetSelectedLanguage
  } = useTranslationState(clipData);

  useEffect(() => {
    if (isOpen) {
      resetSelectedLanguage();
    }
  }, [isOpen, resetSelectedLanguage]);

  const onTranslate = () => {
    if (selectedLanguage) {
      onConfirm(selectedLanguage);
    }
  };

  const sections = useMemo(() => {
    return [
      ...(originalLanguage.length > 0
        ? [
            {
              label: '',
              options: originalLanguage
            }
          ]
        : []),
      ...(availableLanguagesForTranslation.length > 0
        ? [
            {
              label: 'Available Languages',
              options: availableLanguagesForTranslation
            }
          ]
        : []),
      ...(translatedLanguages.length > 0
        ? [
            {
              label: 'Translated Languages',
              options: translatedLanguages
            }
          ]
        : [])
    ];
  }, [originalLanguage, availableLanguagesForTranslation, translatedLanguages]);

  return (
    <ContentDialog size="small" isOpen={isOpen} setIsOpen={onClose} panelClassNames="!overflow-visible">
      <div className="flex flex-col gap-5 px-6 pb-4">
        <div className="flex flex-col space-y-1.5 text-center sm:text-left">
          <h2 className="text-lg font-semibold leading-none tracking-tight">Translate</h2>
          <p className="text-sm text-slate-600">Select a language to translate your transcript and captions into.</p>
        </div>

        <div className="relative space-y-1.5">
          <label className="text-sm font-medium leading-none">Target language</label>
          <SelectDropdown
            selected={selectedLanguage?.name ?? ''}
            onChange={selected => handleLanguageChange(selected)}
            placeholder="Select language"
            sections={sections}
          />
        </div>

        <div className="mt-1 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <Button buttonSize="large" variation="outline" onClick={onClose} trackingId="caption-language-dialog-cancel">
            Cancel
          </Button>
          <Button
            buttonSize="large"
            variation="filled"
            onClick={onTranslate}
            trackingId="caption-language-dialog-translate-button"
            disabled={!selectedLanguage || isDisabled}
          >
            Translate
          </Button>
        </div>
      </div>
    </ContentDialog>
  );
}

export default memo(CaptionLanguageSelectionDialog);
